
import { StateCreator } from 'zustand';
import { UserState } from '@/store/slices/auth/userSlice';
import { DealerState } from './types/stateTypes';
import { transformDealersData } from './dealerTransformers';
import { 
  fetchDealersWithPrices
} from './dealers/dealersActions';
import { 
  fetchFavorites,
  addToFavorites, 
  removeFromFavorites 
} from './favorites/favoritesActions';
import { initializeDealersFromSSR } from './actions/ssrActions';
import { toast } from 'sonner';
import { validateAuthenticationWithExplicitId } from '@/utils/authValidation';
import { debugLog, debugError } from '@/utils/debugLogger';
import { favoritesCache } from '@/lib/cache/favoritesCache';

type StoreWithUser = DealerState & UserState;

export const createDealerSlice: StateCreator<
  StoreWithUser,
  [],
  [],
  DealerState
> = (set, get) => ({
  dealers: [],
  favoritesDealers: [],
  
  fetchDealers: async () => {
    try {
      const { dealersData, latestPrices } = await fetchDealersWithPrices();
      const favoritesDealers = get().favoritesDealers;
      const dealers = transformDealersData(dealersData, favoritesDealers, latestPrices);
      set({ dealers });
    } catch (error) {
      debugError('dealer', 'Error fetching dealers', error, 'dealerSlice');
    }
  },
  
  fetchFavoriteDealers: async () => {
    const { user, isAuthenticated } = get();
    
    const { isValid, userId } = validateAuthenticationWithExplicitId(
      isAuthenticated, 
      user, 
      undefined
    );
    
    if (!userId) {
      debugLog('dealer', 'No valid user ID found, clearing favorites', undefined, 'fetchFavoriteDealers');
      set({ favoritesDealers: [] });
      return [];
    }
    
    try {
      const favoriteIds = await fetchFavorites(userId);
      set({ favoritesDealers: favoriteIds });
      
      const dealers = get().dealers.map(dealer => ({
        ...dealer,
        isFavorite: favoriteIds.includes(dealer.id)
      }));
      
      set({ dealers });
      return favoriteIds;
    } catch (error) {
      debugError('dealer', 'Error fetching favorite dealers', error, 'fetchFavoriteDealers');
      set({ favoritesDealers: [] });
      return [];
    }
  },
  
  toggleFavoriteDealer: async (dealerId, explicitUserId) => {
    const { favoritesDealers, user, isAuthenticated } = get();
    
    const { isValid, userId } = validateAuthenticationWithExplicitId(
      isAuthenticated, 
      user, 
      explicitUserId
    );
    
    if (!userId) {
      toast.error("Autenticazione richiesta", {
        description: "Per gestire i preferiti, devi essere autenticato correttamente."
      });
      return;
    }
    
    const isFavorite = favoritesDealers.includes(dealerId);
    debugLog('dealer', 'Starting toggleFavoriteDealer with optimistic update', { dealerId, userId, isFavorite }, 'toggleFavoriteDealer');
    
    // Save current state for potential rollback
    const previousFavorites = [...favoritesDealers];
    const previousDealers = [...get().dealers];
    
    // OPTIMISTIC UPDATE: Update UI immediately
    const newFavorites = isFavorite
      ? favoritesDealers.filter(id => id !== dealerId)
      : [...favoritesDealers, dealerId];
    
    set({ 
      favoritesDealers: newFavorites,
      dealers: get().dealers.map(d => 
        d.id === dealerId 
          ? { ...d, isFavorite: !isFavorite } 
          : d
      ) 
    });
    
    try {
      let operationSuccess = false;
      
      if (isFavorite) {
        debugLog('dealer', 'Removing from favorites (DB operation)', { dealerId, userId }, 'toggleFavoriteDealer');
        operationSuccess = await removeFromFavorites(userId, dealerId);
      } else {
        debugLog('dealer', 'Adding to favorites (DB operation)', { dealerId, userId }, 'toggleFavoriteDealer');
        try {
          await addToFavorites(userId, dealerId);
          operationSuccess = true;
        } catch (addError) {
          debugError('dealer', 'Failed to add to favorites', addError, 'toggleFavoriteDealer');
          operationSuccess = false;
        }
      }
      
      if (operationSuccess) {
        debugLog('dealer', 'Database operation successful, keeping optimistic update', { dealerId, isFavorite }, 'toggleFavoriteDealer');
        // UI is already updated optimistically, nothing more to do
      } else {
        debugError('dealer', 'Database operation failed, rolling back UI', { dealerId, isFavorite }, 'toggleFavoriteDealer');
        
        // ROLLBACK: Restore previous state
        set({ 
          favoritesDealers: previousFavorites,
          dealers: previousDealers
        });
        
        toast.error("Errore nell'operazione", {
          description: "Non è stato possibile aggiornare i preferiti."
        });
      }
      
    } catch (error) {
      debugError('dealer', 'Error in toggleFavoriteDealer, rolling back', error, 'toggleFavoriteDealer');
      
      // ROLLBACK: Restore previous state
      set({ 
        favoritesDealers: previousFavorites,
        dealers: previousDealers
      });
      
      const errorMessage = (error as any)?.message || '';
      const isDuplicateError = errorMessage.includes('duplicate key') || 
                             (error as any)?.code === '23505';
      
      if (isDuplicateError) {
        debugLog('dealer', 'Duplicate key error, syncing with server', { dealerId }, 'toggleFavoriteDealer');
        // For duplicate errors, just sync with server state
        await get().fetchFavoriteDealers();
      } else {
        // For other errors, invalidate cache and show error
        favoritesCache.invalidate(userId);
        
        toast.error("Errore nell'operazione", {
          description: "Si è verificato un errore. Riprova."
        });
      }
    }
  },
  
  initializeDealersFromSSR: (selectedMetal: 'gold' | 'silver' = 'gold') => {
    initializeDealersFromSSR(set, get, selectedMetal);
  },
});
